/* eslint-disable camelcase */
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormat } from '@nx-smartmonkey/shared/helpers';
import * as XLSX from 'xlsx';

import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../constants';
import { Driver } from '../../domain/driver/Driver';
import { Project } from '../../domain/project/Project';
import { Stop } from '../../domain/stop/Stop';
import { Task } from '../../domain/task/Task';
import {
  TasksTableColumn,
  TasksTableColumnKeys,
} from '../../pages/planner/plan-details/context/tableColumns/tasksTableColumns';
import { intl } from '../IntGlobalProvider';
import { ExcelEntryFormatter, customFieldToFormatter, jsonToExcel } from './Formatters';

const getTasksFormatters = ({
  parsedTableColumns,
  stops,
  drivers,
}: {
  parsedTableColumns: Record<TasksTableColumnKeys, boolean>;
  stops: Stop[];
  drivers: Driver[];
}): ExcelEntryFormatter[] => {
  const formatters: ExcelEntryFormatter[] = [];

  if (parsedTableColumns.label) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.name` }),
      id: `label`,
      formatter: (task: Task) => task.label,
    });
  }
  if (parsedTableColumns.driver_id) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.driver_id` }),
      id: `driver_id`,
      formatter: (task: Task) => {
        const driver = drivers.find((driver) => driver.id === task.driver_id);
        return driver ? driver.label : undefined;
      },
    });
  }
  if (parsedTableColumns.stop_id) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.stop_id` }),
      id: `stop_id`,
      formatter: (task: Task) => {
        const stop = stops.find((stop) => stop.id === task.stop_id);
        return stop ? stop.label : undefined;
      },
    });
  }
  if (parsedTableColumns.stop_order) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.stop_order` }),
      id: `stop_order`,
      formatter: (task: Task) => {
        const stop = stops.find((stop) => stop.id === task.stop_id);
        return stop ? stop.order : undefined;
      },
    });
  }
  if (parsedTableColumns.barcode) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.barcode` }),
      id: `barcode`,
      formatter: (task: Task) => task.barcode,
    });
  }
  if (parsedTableColumns.comments) {
    formatters.push({
      header: intl.formatMessage({ id: `tasks.excel.columns.comments` }),
      id: `comments`,
      formatter: (task: Task) => task.comments,
    });
  }

  if (parsedTableColumns.status) {
    formatters.push({
      id: `status`,
      header: intl.formatMessage({ id: `tasks.excel.columns.status` }),
      formatter: (task: Task) =>
        task.status
          ? intl.formatMessage({
              id: `tasks.status.${task.status}`,
            })
          : undefined,
    });
  }

  if (parsedTableColumns.created_at) {
    formatters.push({
      id: `created_at`,
      header: intl.formatMessage({ id: `tasks.excel.columns.created_at` }),
      formatter: (task: Task) => dateFormat(task.created_at),
      type: `d`,
    });
  }

  return formatters;
};

export const exportTasks = ({
  tasks,
  stops,
  drivers,
  project,
  tableColumns,
}: {
  tasks: Task[];
  stops: Stop[];
  drivers: Driver[];
  project: Project;
  tableColumns?: Array<TasksTableColumn>;
}) => {
  const customFields: CustomFieldDefinition[] = [
    ...(project?.custom_fields?.task ?? []),
    ...(project?.custom_fields?.service_report_task_canceled ?? []),
    ...(project?.custom_fields?.service_report_task_completed ?? []),
  ];

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: `Tasks`,
    Subject: `Tasks exported`,
    Author: `Routal`,
    CreatedDate: new Date(),
  };

  const parsedTableColumns = (tableColumns ?? []).reduce(
    (amount, next) => ({
      ...amount,
      [next.identifier]: next.displayable,
    }),
    {} as Record<TasksTableColumnKeys, boolean>
  );

  const tasksCustomFieldFormatter = customFields
    .filter((cf) => (parsedTableColumns as any)[`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${cf.custom_id}`] === true)
    .filter((cf) => cf.field_model === `task` && project.planner.taskHasCustomField(cf.custom_id))
    .filter(
      (cf) =>
        cf.field_model === `service_report_task_canceled` && project.drivers.taskCanceledHasCustomField(cf.custom_id)
    )
    .filter(
      (cf) =>
        cf.field_model === `service_report_task_completed` && project.drivers.taskCompletedHasCustomField(cf.custom_id)
    )
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (task: Task) => formatter.formatter && formatter.formatter(task.custom_fields),
      };
    });

  const allTasksFormatter = [
    ...getTasksFormatters({ parsedTableColumns, stops, drivers }),
    ...tasksCustomFieldFormatter,
  ];

  const tasksSheet = jsonToExcel(tasks, allTasksFormatter);

  const tasksLabel = intl.formatMessage({ id: `tasks.excel.sheets.tasks` });
  wb.SheetNames.push(tasksLabel);
  wb.Sheets[tasksLabel] = tasksSheet;

  return XLSX.write(wb, { bookType: `xlsx`, type: `binary`, cellDates: true });
};
